import { SessionContext } from "@/context/SessionContext";
import { ProjectSubmission } from "@/javascript/project-objects";
import { Badge, Button, Group, Modal, Stack, Table, Text, Title, useMantineTheme } from "@mantine/core";
import { IconArrowDown, IconMusic, IconUser } from "@tabler/icons-react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import ProjectSubmissionVideoPlayer from "./ProjectSubmissionVideoPlayer";

type ProjectSubmissionVideoModalProps = {
	projectSubmission: ProjectSubmission,
	isOpen: boolean,
	setShowModal: (show: boolean) => void;
};

const ProjectSubmissionVideoModal: React.FC<ProjectSubmissionVideoModalProps> = ({ projectSubmission, isOpen, setShowModal }) => {
	const theme = useMantineTheme();
	const meetTheBandRef = useRef<HTMLTableElement>(null);
	const performerCommentsRef = useRef<HTMLDivElement>(null);

	const handleClose = () => {
		setShowModal(false);
	};

	const session = useContext(SessionContext);
	if (session === undefined) {
		throw new Error("VideoModal must be a child of SessionContext.");
	}

	const [profileAvatars, setProfileAvatars] = useState<Map<string, JSX.Element>>(new Map());

	const fetchProfileAvatars = useCallback(async () => {
		const profileAvatarsMap = new Map<string, JSX.Element>();
		await Promise.all(projectSubmission.instrumentalistTasks.map(async task => {
			if (task.instrumentalist == null) return;
			const avatar = await session.fetchProfileAvatar(task.instrumentalist);
			profileAvatarsMap.set(task.instrumentalist.id, avatar);
		}));
		setProfileAvatars(profileAvatarsMap);
	}, [projectSubmission, session]);

	// Fetch profile images for all instrumentalists in the project submission
	useEffect(() => {
		if (!isOpen) return;
		fetchProfileAvatars();
	}, [fetchProfileAvatars, isOpen]);

	if (!isOpen) return null;

	return (
		<Modal
			opened={isOpen}
			withCloseButton
			radius={25}
			size={"90%"}
			onClose={handleClose}
			title={
				<Group justify="space-between" align="center" style={{ marginLeft: 20 }}>
					<Text fz={28} fw={500}>
						{projectSubmission.submissionProjectName || "A project"}
					</Text>
					{projectSubmission.projectOwner && (
						<Group gap={4}>
							<Text fz={20} c={theme.colors.gray[6]}>
								by
							</Text>
							<Text fz={20} c={theme.colors.gray[6]}>
								{projectSubmission.projectOwner?.username}
							</Text>
						</Group>
					)}
				</Group>
			}
		>
			<Stack align={"center"} gap="md" h="100%">
				<ProjectSubmissionVideoPlayer id={projectSubmission.id} width={"90%"} type="performance" />
			</Stack>

			{projectSubmission.instrumentalistTasks.length > 0 && (
				<Stack w="100%" p={10} align={"center"} gap="md">
					<div style={{ width: "100%", borderTop: `1px solid ${theme.colors.gray[3]}` }} ref={meetTheBandRef} />

					<Button variant="subtle"
						onClick={() => meetTheBandRef.current?.scrollIntoView({ behavior: 'smooth' })}
					>
						<Group>
							<Title order={3} c={theme.colors.blue[7]}>
								Meet the Band
							</Title>
							<IconArrowDown size={24} color={theme.colors.blue[7]} />
						</Group>
					</Button>

					<Table w="100%" striped highlightOnHover stickyHeader>
						<Table.Thead>
							<Table.Tr>
								<Table.Th><Group><Title order={4}>Instrument</Title><IconMusic size={16} /></Group></Table.Th>
								<Table.Th><Group><Title order={4}>Performer</Title><IconUser size={16} /></Group></Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{projectSubmission.instrumentalistTasks.map((task, idx) => (
								<Table.Tr key={idx}>
									<Table.Td>
										<Text>
											{task.instrument.name}
										</Text>
									</Table.Td>
									<Table.Td>
										{task.instrumentalist == null ? null : (
											<Group w="100%" justify="left" align="center" gap="xs">
												{profileAvatars.get(task.instrumentalist.id)}
												<Group gap={4}>
													<Text>
														{task.instrumentalist.firstName}
													</Text>
													<Text>
														{task.instrumentalist.lastName}
													</Text>
												</Group>
												{task.instrumentalist.location && (
													<Badge
														size="md"
														variant="gradient"
														gradient={{ from: task.instrumentalist.location.primaryColor || 'blue', to: task.instrumentalist.location.secondaryColor || 'blue', deg: 45 }}
													>
														{task.instrumentalist.location.displayName} Musician
													</Badge>
												)}
											</Group>
										)}
									</Table.Td>
								</Table.Tr>
							))}
						</Table.Tbody>
					</Table>
				</Stack>
			)}

			{projectSubmission.feedbackStatus === "Uploaded" && (
				<Stack mt={20} align={"center"} gap="md">
					<div style={{ width: "100%", borderTop: `1px solid ${theme.colors.gray[3]}` }} ref={performerCommentsRef} />

					<Button variant="subtle"
						onClick={() => performerCommentsRef.current?.scrollIntoView({ behavior: 'smooth' })}
					>
						<Group>
							<Title order={3} c={theme.colors.blue[7]}>
								Performer Comments
							</Title>
							<IconArrowDown size={24} color={theme.colors.blue[7]} />
						</Group>
					</Button>

					<ProjectSubmissionVideoPlayer id={projectSubmission.id} width={"80%"} type="feedback" />
				</Stack>
			)}
		</Modal>
	);
};

export default ProjectSubmissionVideoModal;
